import React, { useEffect, useRef, useState } from 'react';
import { AlertTriangle, Loader2, Trash2 } from 'lucide-react';
import Button from '../../components/ui/Button';
import { ValueChange } from '../../components/ui/ValueChange';
import type { SyncPreview, SyncPreviewCard, SyncPreviewField } from '../../lib/vendorInvoiceApi';
// The one owner of "what does this catalogue row cost per kg / L / item"
// (BUSINESS_RULES#catalogue-row-unit-cost-ttd) — the SAME call
// `MasterCataloguePage`'s Per kg / L and Per Pack cells make. The card sends
// the raw facts (`SyncPreviewCard.priceFacts`); this reads them the exact
// same way rather than a second per-kg rule living on this card.
// `altPricingFor` is the grid's OWN "pack size read out of the item name"
// derivation (see its doc comment) — without it a NAME-priced pack showed on
// the grid and nowhere on this card.
import {
  altPricingFor,
  catalogueRowIsTtd,
  costPriceTiersTtd,
  formatCostTierTtd as formatTtd,
  readPackSize,
} from '../catalogue/costPriceTiers';
import { getExchangeRate, getVatRate } from '../../lib/settingsApi';
import { createItemCategory, listItemCategories } from '../../lib/itemCategoryApi';
import { normalizeCategoryKey } from '../../utils/itemCategoryKey';
import { offersCategory } from './categoryChoices';
// Reused only to tell a LENGTH size ('50 MTR') apart — never a second size
// parser. `readPackSize` (costPriceTiers) answers "mass or volume"; a size
// `measureFromText` reads in a unit of the client's own length table
// (`METRE_PER_UNIT`) is a length.
import { measureFromText } from '../../utils/unitVocabulary';
import { METRE_PER_UNIT } from '../../utils/packConversion';

/**
 * WHAT CONFIRM & SYNC WILL CHANGE IN THE MASTER CATALOGUE, before it changes it.
 *
 * One card per catalogue item the invoice touches. Every value comes from a dry
 * run of the real sync (`GET /vendor-invoices/:id/confirm-inventory/preview`),
 * so a card cannot promise a change the sync would not make: a parked or held
 * line shows the price the row keeps, with what the invoice says beside it.
 *
 * The switch on each card takes that line out of the catalogue sync. Stock is
 * unaffected — every line is still received (Preet, 2026-09-10).
 *
 * This panel renders inside the invoice review, above Line Items, rather than
 * as a second dialog over the full-screen one: stacking two modals is how the
 * z-index ladder in docs/DESIGN.md grew in the first place.
 */

const OUTCOME: Record<SyncPreviewCard['outcome'], { label: string; chip: string }> = {
  new: { label: 'New item', chip: 'border-emerald-200 bg-emerald-50 text-emerald-800' },
  update: { label: 'Update', chip: 'border-primary-500/40 bg-primary-50 text-primary-600' },
  parked: { label: 'Price parked', chip: 'border-amber-200 bg-amber-50 text-amber-800' },
  kept: { label: 'Price kept', chip: 'border-slate-200 bg-slate-100 text-slate-700' },
  excluded: { label: 'Excluded', chip: 'border-slate-200 bg-white text-slate-500' },
};

const percent = (value: number | null | undefined) =>
  value == null ? '' : ` (${value > 0 ? '+' : ''}${Math.round(value)}%)`;

function Field({ field }: { field: SyncPreviewField }) {
  const tone = field.changed
    ? 'bg-emerald-50 ring-1 ring-inset ring-emerald-200'
    : field.proposed
      ? 'bg-amber-50 ring-1 ring-inset ring-amber-200'
      : 'bg-slate-50';

  return (
    <div className={`flex items-baseline justify-between gap-3 rounded px-2 py-1 text-xs ${tone}`}>
      <dt className="font-medium text-slate-500">{field.label}</dt>
      <dd className="min-w-0 text-right text-slate-700">
        {field.changed ? (
          <ValueChange from={field.from} to={field.to} />
        ) : field.proposed ? (
          <span className="tabular-nums">
            {field.to || '—'}
            <span className="ml-2 text-slate-500">
              invoice says {field.proposed}
              {percent(field.changePct)}
            </span>
          </span>
        ) : (
          <span className="tabular-nums">{field.to || '—'}</span>
        )}
      </dd>
    </div>
  );
}

/**
 * One highlighted row of the price block: a label, an optional bold TT$
 * value, in the block's own colour.
 *
 * `grey` is the informational tone (2026-09-25): a fact about the row that
 * is not a data gap to fix — the exchange rate is still loading, or the row
 * is sold by length and simply has no per-kg concept. `amber` stays for a
 * gap a reviewer can act on. No `value` prints only the label, full width —
 * every WHY sentence below is prose, not a figure.
 */
function PriceRow({
  label,
  value,
  tone,
}: {
  label: string;
  value?: string;
  tone: 'violet' | 'amber' | 'grey';
}) {
  const toneClass =
    tone === 'violet'
      ? 'bg-violet-50 ring-1 ring-inset ring-violet-200 text-violet-700'
      : tone === 'amber'
        ? 'bg-amber-50 ring-1 ring-inset ring-amber-200 text-amber-900'
        : 'bg-slate-50 ring-1 ring-inset ring-slate-200 text-slate-600';
  const valueClass = tone === 'violet' ? 'text-violet-900' : tone === 'amber' ? 'text-amber-900' : 'text-slate-700';
  return (
    <div className={`flex items-baseline justify-between gap-3 rounded px-2 py-1 text-xs ${toneClass}`}>
      <dt className="font-medium">{label}</dt>
      {value ? <dd className={`font-bold tabular-nums ${valueClass}`}>{value}</dd> : null}
    </div>
  );
}

/** A stated size the tiers cannot use because it is a LENGTH ('50 MTR'), never a weight or a volume. */
function isLengthOnlySize(sizeText: string | null | undefined): boolean {
  const parsed = measureFromText(sizeText);
  return parsed != null && METRE_PER_UNIT[parsed.unit] != null;
}

/**
 * THE PRICE BLOCK, impossible to miss (the owner, 2026-09-25): Per kg (or Per
 * L for a volume size) and Per item, in the SAME figures — same owner, same
 * rounding, same currency, same VAT basis — the Master Catalogue grid would
 * show for this row. Never a second per-kg rule: `card.priceFacts` carries
 * the raw facts, `altPricingFor` builds the SAME "pack read off the item
 * name" derivation the grid builds, and `costPriceTiersTtd` (the one owner,
 * BUSINESS_RULES#catalogue-row-unit-cost-ttd) does the arithmetic, exactly as
 * `MasterCataloguePage` calls it for its own Per kg / L and Per Pack cells —
 * `weightFromName` left at its default `false`, the grid's own setting, not
 * the quotation popup's.
 *
 * Always coloured, distinctly from the emerald "changed" / amber "proposed"
 * tones above: violet. When neither a kg nor an L figure exists, the block
 * says WHY instead of guessing "no size stated" — see the comment inside —
 * and Per item still shows beside it when the row states more than one item
 * to the pack.
 */
function PriceBlock({
  priceFacts,
  usdToTtd,
  rateLoading,
  vatRatePercent,
  vatLoading,
}: {
  priceFacts: SyncPreviewCard['priceFacts'];
  usdToTtd: number | null;
  /** Still fetching the settings exchange rate — see the WHY block below. */
  rateLoading: boolean;
  /** The Settings VAT rate (D63), passed to the owner exactly as the grid passes it. */
  vatRatePercent: number | null;
  vatLoading: boolean;
}) {
  if (!priceFacts) return null;
  const alt = altPricingFor(priceFacts, usdToTtd, priceFacts.pricingDetails);
  const tiers = costPriceTiersTtd(priceFacts, usdToTtd, alt, vatRatePercent);
  const perItem = tiers.pack != null ? <PriceRow label="Per item" value={formatTtd(tiers.pack)} tone="violet" /> : null;

  // WHETHER TO SHOW AMBER IS THE TIERS' QUESTION, NOT THE SIZE TEXT'S. A row
  // can state no `unit_size` and no pack facts and still carry a kg/L price —
  // `priceLevels.byUnit`, off the item's OWN unit conversions — and that price
  // is exactly what the grid's "Per kg / L" column would print (unit PCS, no
  // unit_size, `priceLevels.byUnit: [{unit:'L', ttAmount:14.07}]` prints
  // TT$14.07 / L there). Deciding from `unit_size` alone showed amber over a
  // real figure.
  //
  // BUT THE OLD AMBER SENTENCE LIED whenever a size WAS stated and the block
  // still had nothing to show (the owner, 2026-09-25, on a "White Quinoa, 5kg
  // Bag" priced 12 to the case with nobody typing 12 into Units / Case): it
  // said "no size stated" over a row that plainly states one. Both tiers null
  // now says WHY, in order, never re-spelling a rule that already has an
  // owner elsewhere:
  //
  //   1. A US$ row with no exchange rate loaded yet — every figure below needs
  //      it (`altPricingFor` / `costPriceTiersTtd`'s own TT$ → USD detour), so
  //      this is checked before anything else. Grey while the fetch is still
  //      in flight (it will resolve itself); amber once it has settled with
  //      no usable rate (failed, or `getExchangeRate` answered nothing) — a
  //      TT$-priced row needs no rate at all and never reaches this branch.
  //   2. A stated size that reads as a LENGTH ('50 MTR') — not a data gap to
  //      fix: a length genuinely has no per-kg figure, so grey.
  //   3. A stated weight or volume with still no figure — the size IS stated,
  //      the missing fact is how many items one pack holds (a CASE of "5 KG"
  //      with no Units / Case), so amber, naming that.
  //   4. Only once none of the above answers is it genuinely unsized, and the
  //      amber sentence is honest again.
  if (tiers.kg == null && tiers.l == null) {
    const isTtd = catalogueRowIsTtd(priceFacts);
    if (!isTtd && usdToTtd == null) {
      return (
        <div className="grid gap-1 px-2 pt-2">
          <PriceRow
            label="TT$ figures need the exchange rate"
            tone={rateLoading ? 'grey' : 'amber'}
          />
          {perItem}
        </div>
      );
    }
    // A VAT-applicable row with no Settings rate: the owner refuses every tier
    // (D63, never a guessed rate), so it is the rate that is missing, not the size.
    if (priceFacts.vat_applicable === true && vatRatePercent == null) {
      return (
        <div className="grid gap-1 px-2 pt-2">
          <PriceRow label="TT$ figures need the VAT rate in Settings" tone={vatLoading ? 'grey' : 'amber'} />
        </div>
      );
    }

    // Steps 2-3, read off the row's stated size through the catalogue's own
    // size reader (`readPackSize`) and the client's length table
    // (`METRE_PER_UNIT`). NOT `perPackBlankReason`: that answers why the PER
    // PACK cell is blank — "priced per PCS, so Per Case already…" — which is
    // a different question, about cells this card never prints (review,
    // 2026-09-25).
    const sizeText = priceFacts.unit_size || priceFacts.packFacts?.unitSize;
    if (isLengthOnlySize(sizeText)) {
      return (
        <div className="grid gap-1 px-2 pt-2">
          <PriceRow label="Sold by length — no per kg" tone="grey" />
          {perItem}
        </div>
      );
    }
    if (readPackSize(sizeText)?.dimension) {
      // A weight or volume IS stated and still no figure: what one pack holds
      // is the missing fact (a CASE of "5 KG" with no Units / Case).
      return (
        <div className="grid gap-1 px-2 pt-2">
          <PriceRow label="Size stated, but not how many items one pack holds" value="per kg unknown" tone="amber" />
          {perItem}
        </div>
      );
    }

    return (
      <div className="grid gap-1 px-2 pt-2">
        <PriceRow label="No size stated" value="per kg unknown" tone="amber" />
        {perItem}
      </div>
    );
  }

  // Exactly one of the pair is the ordinary case (`costPriceTiersTtd`'s "ONE
  // ROW, ONE DIMENSION" rule — a liquid size zeroes kg, a solid size zeroes
  // L) and is labelled by which one it is. Both non-null is the rare row with
  // no readable size but two independently stored tiers; kept generic and
  // kg-first, same as this block always has.
  const label = tiers.kg != null && tiers.l != null ? 'Per kg / L' : tiers.kg != null ? 'Per kg' : 'Per L';
  const value = tiers.kg ?? tiers.l;

  return (
    <div className="grid gap-1 px-2 pt-2">
      <PriceRow label={label} value={formatTtd(value)} tone="violet" />
      {perItem}
    </div>
  );
}

type CategoryOption = { key: string; label: string };
const NEW_CATEGORY = '__new__';

const CATEGORY_HINT: Record<'ai' | 'none' | 'unavailable', string> = {
  ai: 'Suggested by AI',
  none: 'No suggestion, pick one',
  unavailable: 'AI unavailable, pick one',
};

/**
 * D110.3 (owner, 2026-10-08): the category a NEW row will be filed under. The
 * AI picked it from the existing ones; the reviewer can change it, or (with
 * Settings edit, because creating one is an admin act) add one. A new category
 * is added to the page-wide list so the next card offers it too.
 */
function CategoryPicker({
  card,
  options,
  value,
  onChange,
  canCreate,
  onCreated,
}: {
  card: SyncPreviewCard;
  options: CategoryOption[];
  value: string;
  onChange: (lineIndex: number, key: string) => void;
  canCreate: boolean;
  onCreated: (category: CategoryOption) => void;
}) {
  const [adding, setAdding] = useState(false);
  const [label, setLabel] = useState('');
  const [saving, setSaving] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const id = `category-${card.lineIndex}`;

  const choose = (category: CategoryOption) => {
    onCreated(category);
    onChange(card.lineIndex, category.key);
    setAdding(false);
    setLabel('');
  };

  const add = async () => {
    const name = label.trim();
    if (!name || saving) return;
    setSaving(true);
    setProblem(null);
    try {
      const res = await createItemCategory({ label: name });
      choose({ key: res.data.category.key, label: res.data.category.label });
    } catch (e: any) {
      // Codes only, never the server's prose (invariant 6).
      if (e?.code === 'CATEGORY_EXISTS') {
        const key = normalizeCategoryKey(name);
        let existing = options.find((o) => o.key === key || o.label.toLowerCase() === name.toLowerCase());
        if (!existing) {
          try {
            const list = await listItemCategories();
            existing = list.data.categories.find((c) => c.key === key || c.label.toLowerCase() === name.toLowerCase());
          } catch (listError) {
            console.error('Error reloading item categories:', listError);
          }
        }
        if (existing) choose({ key: existing.key, label: existing.label });
        else setProblem('That category already exists. Pick it from the list.');
      } else if (e?.status === 403) {
        setProblem('Only an administrator can create categories.');
      } else {
        setProblem('The category could not be created.');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border-t border-slate-100 px-3 py-2 text-xs">
      <div className="flex items-center justify-between gap-2">
        <label htmlFor={id} className="font-medium text-slate-500">
          Category
        </label>
        <select
          id={id}
          value={adding ? NEW_CATEGORY : value}
          onChange={(event) => {
            if (event.target.value === NEW_CATEGORY) {
              setAdding(true);
              return;
            }
            setAdding(false);
            setProblem(null);
            onChange(card.lineIndex, event.target.value);
          }}
          className="min-w-0 max-w-[60%] rounded border border-slate-300 bg-white px-1.5 py-1 text-xs text-slate-800"
        >
          <option value="">No category</option>
          {options.map((o) => (
            <option key={o.key} value={o.key}>
              {o.label}
            </option>
          ))}
          {canCreate ? <option value={NEW_CATEGORY}>New category…</option> : null}
        </select>
      </div>
      {card.categorySuggestion ? (
        <p className="mt-1 text-xs text-slate-500">{CATEGORY_HINT[card.categorySuggestion]}</p>
      ) : null}
      {adding ? (
        <div className="mt-1.5 flex items-center gap-1.5">
          <input
            type="text"
            aria-label="New category name"
            value={label}
            onChange={(event) => setLabel(event.target.value)}
            className="min-w-0 flex-1 rounded border border-slate-300 px-1.5 py-1 text-xs"
          />
          <Button size="sm" loading={saving} disabled={!label.trim()} onClick={add}>
            Add category
          </Button>
        </div>
      ) : null}
      {problem ? <p className="mt-1 text-xs text-red-700">{problem}</p> : null}
    </div>
  );
}

function Card({
  card,
  excluded,
  onToggle,
  usdToTtd,
  rateLoading,
  vatRatePercent,
  vatLoading,
  categoryOptions,
  categoryValue,
  onCategoryChange,
  canCreateCategory,
  onCategoryCreated,
}: {
  card: SyncPreviewCard;
  excluded: boolean;
  onToggle: (lineIndex: number) => void;
  categoryOptions: CategoryOption[];
  categoryValue: string;
  onCategoryChange: (lineIndex: number, key: string) => void;
  canCreateCategory: boolean;
  onCategoryCreated: (category: CategoryOption) => void;
  usdToTtd: number | null;
  rateLoading: boolean;
  vatRatePercent: number | null;
  vatLoading: boolean;
}) {
  // The SERVER excludes this line regardless of the switch (a credit note, or
  // a packaging-unchecked line) — never offered as a choice, so it reads as
  // excluded whatever the reviewer's own toggle says.
  const forced = card.forcedExcluded === true;
  const effectiveExcluded = excluded || forced;
  const outcome = OUTCOME[effectiveExcluded ? 'excluded' : card.outcome] || OUTCOME.update;
  const note = card.fields.find((field) => field.note)?.note || null;

  return (
    <article
      className={`flex min-w-0 flex-col rounded-lg border border-slate-200 ${effectiveExcluded ? 'bg-slate-50' : 'bg-white'}`}
    >
      <header className="flex items-start justify-between gap-2 border-b border-slate-100 px-3 py-2">
        <div className="min-w-0">
          <p className={`truncate text-[13px] font-semibold ${effectiveExcluded ? 'text-slate-500' : 'text-slate-900'}`}>
            {card.itemName || `Line ${card.lineIndex + 1}`}
          </p>
          <p className="text-xs text-slate-500">
            {card.catalogueId ? 'Existing catalogue item' : 'Not in the catalogue yet'}
          </p>
        </div>
        <span
          className={`inline-flex h-5 shrink-0 items-center whitespace-nowrap rounded-full border px-2 text-xs font-medium ${outcome.chip}`}
        >
          {outcome.label}
        </span>
      </header>

      {/*
       * EXCLUDED means nothing is written, so the block has to read the row
       * AS IT STANDS NOW (`priorPriceFacts`) — never `priceFacts`, which is
       * what the sync WOULD have written had the switch stayed on (the
       * owner, 2026-09-25). A brand-new row has no "as it stands now" to
       * read at all (`priorPriceFacts` is null there), so that case gets a
       * plain note instead of a block with nothing to draw. A server-forced
       * exclusion reads exactly the same way — `priceFacts` there is still
       * "the row as it stands", not a write that is about to happen.
       */}
      {effectiveExcluded && !card.priorPriceFacts ? (
        <p className="px-3 pt-2 text-xs leading-snug text-slate-500">Not written — no catalogue row</p>
      ) : (
        <PriceBlock
          priceFacts={effectiveExcluded ? card.priorPriceFacts : card.priceFacts}
          usdToTtd={usdToTtd}
          rateLoading={rateLoading}
          vatRatePercent={vatRatePercent}
          vatLoading={vatLoading}
        />
      )}

      <dl className="grid gap-1 p-2">
        {card.fields.map((field) => (
          <Field key={field.key} field={field} />
        ))}
      </dl>

      {note ? <p className="px-3 pb-2 text-xs leading-snug text-amber-800">{note}</p> : null}
      {forced ? (
        <p className="px-3 pb-2 text-xs leading-snug text-slate-500">{card.forcedExcludedReason}</p>
      ) : effectiveExcluded ? (
        <p className="px-3 pb-2 text-xs leading-snug text-slate-500">
          Switched off: nothing on this card reaches the Master Catalogue. The goods are still received.
        </p>
      ) : null}

      {offersCategory(card, []) && !effectiveExcluded ? (
        <CategoryPicker
          card={card}
          options={categoryOptions}
          value={categoryValue}
          onChange={onCategoryChange}
          canCreate={canCreateCategory}
          onCreated={onCategoryCreated}
        />
      ) : null}

      <label className="mt-auto flex cursor-pointer items-center gap-2 border-t border-slate-100 px-3 py-1.5 text-xs font-medium text-slate-700">
        <input
          type="checkbox"
          checked={!effectiveExcluded}
          onChange={() => {
            // Never offered as a choice: the server's own exclusion does not
            // toggle, whatever is clicked (the input is also `disabled` below
            // — this is a second guard, not the only one).
            if (!forced) onToggle(card.lineIndex);
          }}
          disabled={forced}
          title={forced ? card.forcedExcludedReason || undefined : undefined}
          className="h-4 w-4 rounded border-slate-300 text-primary-500 focus:ring-primary-500 disabled:cursor-not-allowed disabled:opacity-60"
        />
        Include in this sync
      </label>
    </article>
  );
}

export interface SyncPreviewPanelProps {
  preview: SyncPreview | null;
  loading: boolean;
  /** Why the preview could not be loaded, if it could not. */
  error: string | null;
  /** The catalogue moved under a preview that was already on screen. */
  stale: boolean;
  excluded: number[];
  onToggle: (lineIndex: number) => void;
  onConfirm: () => void;
  onCancel: () => void;
  busy: boolean;
  /** D110.3: lineIndex -> chosen category key ('' = none), owned by the page. */
  categoryChoices?: Record<number, string>;
  onCategoryChange?: (lineIndex: number, key: string) => void;
  /** Creating a category is a Settings edit; the option is hidden without it. */
  canCreateCategory?: boolean;
}

export function SyncPreviewPanel({
  preview,
  loading,
  error,
  stale,
  excluded,
  onToggle,
  onConfirm,
  onCancel,
  busy,
  categoryChoices = {},
  onCategoryChange = () => {},
  canCreateCategory = false,
}: SyncPreviewPanelProps) {
  // Categories created from a card this session, offered on every card.
  const [createdCategories, setCreatedCategories] = useState<CategoryOption[]>([]);
  // TT$ per US$, for the same reason `MasterCataloguePage` loads it: a
  // US$-priced row's price block has no TT$ figure until this arrives, never
  // a guessed rate (the fallback that priced a whole catalogue ~8% under
  // while the business rate had moved, BUSINESS_RULES#catalogue-row-unit-cost-ttd).
  // Fetched once per panel open, not per card — every card's price block
  // reads the same settings rate.
  const [usdToTtd, setUsdToTtd] = useState<number | null>(null);
  // Still in flight vs settled-with-nothing: a US$ card's WHY block (the
  // "TT$ figures need the exchange rate" row) reads grey while this is true
  // and amber once it has gone false with `usdToTtd` still null — a fetch
  // that failed, or one that answered no usable rate. `usdToTtd` alone
  // cannot tell those apart; it is null in both states.
  const [rateLoading, setRateLoading] = useState(true);
  // The Settings VAT rate, loaded the way `MasterCataloguePage` loads it: a
  // `vat_applicable` row's tiers are refused, never guessed, while it is null
  // (D63 — `costPriceTiersTtd`'s `vatRatePercent`).
  const [vatRatePercent, setVatRatePercent] = useState<number | null>(null);
  const [vatLoading, setVatLoading] = useState(true);
  useEffect(() => {
    let cancelled = false;
    getVatRate()
      .then((rateData) => {
        if (!cancelled) setVatRatePercent(rateData.ratePercent);
      })
      .catch((error) => {
        console.error('Error fetching VAT rate:', error);
        if (!cancelled) setVatRatePercent(null);
      })
      .finally(() => {
        if (!cancelled) setVatLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);
  useEffect(() => {
    let cancelled = false;
    getExchangeRate()
      .then((rate) => {
        if (!cancelled) {
          setUsdToTtd(rate.usdToTtd && rate.usdToTtd > 0 ? rate.usdToTtd : null);
          setRateLoading(false);
        }
      })
      .catch((error) => {
        // Logged, not swallowed (CLAUDE.md forbids a bare `catch (_) {}`) —
        // the same way `MasterCataloguePage` reports its own exchange-rate
        // fetch failing (`console.error('Error fetching exchange rate:', ...)`).
        // The behaviour is unchanged either way: null means every price
        // block prints "—" rather than a guessed rate.
        console.error('Error fetching exchange rate:', error);
        if (!cancelled) {
          setUsdToTtd(null);
          setRateLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Opened from the button at the TOP of the invoice, drawn below the totals:
  // on a normal screen it appeared off the bottom and the click looked dead
  // (browser QA, 2026-09-25). Brought into view once, when it opens.
  const sectionRef = useRef<HTMLElement>(null);
  useEffect(() => {
    sectionRef.current?.scrollIntoView?.({ behavior: "smooth", block: "start" });
  }, []);

  if (loading) {
    return (
      <section ref={sectionRef} className="scroll-mt-16 rounded-xl border border-slate-200 bg-white px-4 py-6">
        <p className="flex items-center gap-2 text-sm text-slate-600">
          <Loader2 aria-hidden="true" className="h-4 w-4 animate-spin motion-reduce:animate-none" />
          Working out what this invoice will change…
        </p>
      </section>
    );
  }

  if (error) {
    return (
      <section ref={sectionRef} className="scroll-mt-16 rounded-xl border border-red-200 bg-red-50 px-4 py-4">
        <p className="text-sm font-semibold text-red-900">The catalogue preview could not be loaded.</p>
        <p className="mt-1 text-xs text-red-800">{error}</p>
        {/* The way forward the page promised. Without it, Confirm & sync in
            the header only re-opened this same failing preview (browser QA,
            2026-09-25). The sync still runs every server-side gate. */}
        <div className="mt-3 flex gap-2">
          <Button size="sm" onClick={onCancel} disabled={busy}>
            Close
          </Button>
          <Button size="sm" variant="primary" loading={busy} onClick={onConfirm}>
            Confirm without preview
          </Button>
        </div>
      </section>
    );
  }

  if (!preview) return null;

  const blocking = preview.refusals.filter((refusal) => !refusal.overridable);
  const excludedSet = new Set(excluded);
  // A server-forced exclusion (credit note, unchecked packaging) writes
  // nothing either, whatever the switch says.
  const willWrite = preview.cards.filter(
    (card) => !excludedSet.has(card.lineIndex) && card.forcedExcluded !== true
  ).length;
  const asOf = new Date(preview.computedAt);
  const vocabulary: CategoryOption[] = [...(preview.categories ?? [])];
  const known = new Set(vocabulary.map((c) => c.key));
  for (const c of [
    ...createdCategories,
    ...preview.cards.map((card) => card.suggestedCategory).filter((c): c is CategoryOption => !!c),
  ]) {
    if (!known.has(c.key)) {
      known.add(c.key);
      vocabulary.push(c);
    }
  }

  return (
    <section ref={sectionRef} className="scroll-mt-16 rounded-xl border border-slate-200 bg-white" aria-label="Catalogue changes">
      <header className="flex flex-wrap items-start justify-between gap-3 border-b border-slate-200 px-4 py-2.5">
        <div className="min-w-0">
          <h3 className="text-sm font-semibold text-slate-900">Review catalogue changes</h3>
          <p className="mt-0.5 text-xs text-slate-500">
            {preview.cards.length} item{preview.cards.length === 1 ? '' : 's'} from this invoice, as of{' '}
            {asOf.toLocaleTimeString()}. Stock is received exactly as it is today.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" onClick={onCancel} disabled={busy}>
            Back to invoice
          </Button>
          <Button
            size="sm"
            variant="primary"
            loading={busy}
            disabled={blocking.length > 0}
            onClick={onConfirm}
          >
            Confirm &amp; sync to Inventory
          </Button>
        </div>
      </header>

      {stale ? (
        <p className="border-b border-amber-200 bg-amber-50 px-4 py-2 text-xs text-amber-900">
          The catalogue changed while this was open, so these cards were reloaded. Check them again before
          confirming.
        </p>
      ) : null}

      {blocking.length ? (
        <div className="border-b border-red-200 bg-red-50 px-4 py-3">
          <p className="flex items-center gap-2 text-sm font-semibold text-red-900">
            <AlertTriangle aria-hidden="true" className="h-4 w-4" />
            This invoice cannot be sent yet
          </p>
          <ul className="mt-1.5 grid gap-1 pl-6 text-xs text-red-900">
            {blocking.map((refusal) => (
              <li key={refusal.code} className="list-disc">
                {refusal.message}
                {/*
                 * QA USC-BUG-0061 / 0064: NAME THE CELL, not just the line.
                 * Every gate in previewVendorInvoiceConfirm sends a per-line
                 * `detail` — the column for a required/stock cell, the item
                 * name for pack math and expiry, the printed text for a unit
                 * it cannot read. This list used to print "(line 3)" and throw
                 * the detail away, so the reviewer was told a cell was empty
                 * but not which one. One list, so every gate reads the same.
                 */}
                {refusal.lines.length ? (
                  <ul className="mt-0.5 grid gap-0.5 pl-1 text-red-800">
                    {refusal.lines.map((line) => (
                      <li key={line.lineNo}>
                        Line {line.lineNo}
                        {line.detail ? ` — ${line.detail}` : ''}
                      </li>
                    ))}
                  </ul>
                ) : null}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {preview.removedRows.length ? (
        <p className="flex items-start gap-2 border-b border-amber-200 bg-amber-50 px-4 py-2 text-xs text-amber-900">
          <Trash2 aria-hidden="true" className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>
            {preview.removedRows.length} old catalogue row
            {preview.removedRows.length === 1 ? '' : 's'} will be removed, left behind by a renamed line:{' '}
            {preview.removedRows.map((row) => row.itemName || row.id).join(', ')}.
          </span>
        </p>
      ) : null}

      <div className="grid gap-2 p-3 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4">
        {preview.cards.map((card) => (
          <Card
            key={card.lineIndex}
            card={card}
            excluded={excludedSet.has(card.lineIndex)}
            onToggle={onToggle}
            usdToTtd={usdToTtd}
            rateLoading={rateLoading}
          vatRatePercent={vatRatePercent}
          vatLoading={vatLoading}
            categoryOptions={vocabulary}
            categoryValue={categoryChoices[card.lineIndex] ?? ''}
            onCategoryChange={onCategoryChange}
            canCreateCategory={canCreateCategory}
            onCategoryCreated={(c) =>
              setCreatedCategories((prev) => (prev.some((p) => p.key === c.key) ? prev : [...prev, c]))
            }
          />
        ))}
      </div>

      <footer className="flex flex-wrap items-center justify-between gap-2 border-t border-slate-200 px-4 py-2 text-xs text-slate-600">
        <span>
          {willWrite} of {preview.cards.length} item{preview.cards.length === 1 ? '' : 's'} will be written
          {preview.counts.skipped ? `, ${preview.counts.skipped} line(s) have no price` : ''}
          {preview.counts.merged ? `, ${preview.counts.merged} line(s) merged into another` : ''}.
        </span>
        <span>Stock moves once, and cannot be undone here.</span>
      </footer>
    </section>
  );
}

export default SyncPreviewPanel;
